'use client';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, accordionSummaryClasses, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faMemoCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import RenderFormFields from 'src/components/era-package/forms/RenderFormFields';
import { RenderArrayField } from 'src/components/era-package/forms/RenderArrayField';
import { useFormConfigContext } from './hooks/useFormConfig';

const RenderFormSection = ({
  section, tabKey, arrayKey,
  optionsMap,
  formData,
  setFormData, forceFormData,
  errors,
  setErrors,
  setHasUnsaved,
  globalAccordionAction,
  readOnly = false,
  dense, spacing = 2.5,
  sx, children, actions
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const rhf = useFormConfigContext() || null

  // Effect to handle global accordion action changes
  useEffect(() => {
    if (globalAccordionAction?.type === 'expand') {
      setExpanded(true);
    } else if (globalAccordionAction?.type === 'collapse') {
      setExpanded(false);
    }
  }, [globalAccordionAction]);

  const renderSectionContent = () => {
    if (section.type === 'table') {
      return (
        <RenderArrayField
          section={section}
          tabKey={tabKey}
          optionsMap={optionsMap}
          readOnly={readOnly}
          data={section.data}
        />
      );
    }
    else {
      // Render regular form fields
      return (
        <RenderFormFields
          fields={section.fields}
          optionsMap={optionsMap}
          arrayKey={arrayKey}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          setHasUnsaved={setHasUnsaved}
          readOnly={readOnly}
          forceFormData={forceFormData}
          dense={dense}
          spacing={spacing}
        />
      );
    }
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  // Check if section should be rendered based on condition
  if (section.condition && typeof section.condition === 'function') {
    const shouldRender = section.condition(
      !!rhf ? rhf.getValues() : formData,
      rhf?.getValues(arrayKey) || null,
    );
    if (!shouldRender) {
      return null;
    }
  }

  return (
    <Accordion expanded={expanded} onChange={handleAccordionChange} elevation={section.disableGutters ? 0 : 1} sx={{
      ...section.disableGutters && { border: 0 },
      ...sx
    }}>
      {!section.headless &&
        <AccordionSummary
          expandIcon={<FontAwesomeIcon icon={faAngleDown} />}
          aria-controls={section.title}
          title={`${expanded ? 'Collapse' : 'Expand'} ${section.title}`}
          sx={{
            minHeight: '2rem',
            backgroundColor: theme.palette.primary[50],
            '&.Mui-expanded': {
              minHeight: '2rem',
            },
            [`> .${accordionSummaryClasses.content}.Mui-expanded`]: {
              margin: '0.75rem 0',
            },
          }}
        >
          <Typography mr={1} color={theme.palette.primary.main}>
            {section.icon || <FontAwesomeIcon icon={faMemoCircleInfo} />}
          </Typography>
          <Typography variant="h6" color={theme.palette.primary.main}>
            {section.title}
          </Typography>
        </AccordionSummary>
      }
      <AccordionDetails sx={{ p: section.disableGutters ? 0 : 3 }}>
        {renderSectionContent()}
        {children}
      </AccordionDetails>
      {actions && <AccordionActions sx={{ px: section.disableGutters ? 0 : 3, pt: 0, pb: 2 }}>
        {actions}
      </AccordionActions>}
    </Accordion>
  );
};

export default RenderFormSection;
